import {LocalStorage} from '@chekin/core';

const DEVICE_MEMORY_THRESHOLD_MB = 4;
const HARDWARE_CONCURRENCY_THRESHOLD = 4;
const NETWORK_RTT_THRESHOLD_MS = 150;
const SERVER_MODE_KEY = 'liveness_use_server_mode';

// Type augmentation for navigator
interface NavigatorExtended extends Navigator {
  deviceMemory?: number;
  connection?: {
    type?: string;
    saveData?: boolean;
    rtt?: number;
  };
}

interface CanUseClientSideDetectionOptions {
  forceLivenessMechanism?: 'AUTO' | 'SERVER' | 'CLIENT';
  setIsWebGLSupported?: (isSupported: boolean) => void;
}

export const canUseClientSideDetection = ({
  forceLivenessMechanism = 'AUTO',
  setIsWebGLSupported,
}: CanUseClientSideDetectionOptions = {}): boolean => {
  if (forceLivenessMechanism === 'CLIENT') return true;
  if (forceLivenessMechanism === 'SERVER') return false;

  const savedPreference = LocalStorage.get(SERVER_MODE_KEY);
  if (savedPreference !== null) {
    return savedPreference === 'false';
  }

  try {
    const extendedNavigator = navigator as NavigatorExtended;

    const hasGoodMemory =
      typeof extendedNavigator.deviceMemory === 'undefined' ||
      extendedNavigator.deviceMemory >= DEVICE_MEMORY_THRESHOLD_MB;

    const hasGoodCores =
      !('hardwareConcurrency' in extendedNavigator) ||
      extendedNavigator.hardwareConcurrency >= HARDWARE_CONCURRENCY_THRESHOLD;

    let webGLSupported = false;
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      webGLSupported = Boolean(gl);

      if (setIsWebGLSupported) {
        setIsWebGLSupported(webGLSupported);
      }
    } catch {
      webGLSupported = false;
      if (setIsWebGLSupported) {
        setIsWebGLSupported(false);
      }
    }

    const networkInfo = extendedNavigator.connection;
    let hasGoodNetwork = true;

    if (networkInfo) {
      hasGoodNetwork = networkInfo.type !== 'cellular' && !networkInfo.saveData;

      if (typeof networkInfo.rtt !== 'undefined') {
        hasGoodNetwork = hasGoodNetwork && networkInfo.rtt <= NETWORK_RTT_THRESHOLD_MS;
      }
    }

    const canUseClientSide =
      hasGoodMemory && hasGoodCores && webGLSupported && hasGoodNetwork;

    LocalStorage.set(SERVER_MODE_KEY, (!canUseClientSide).toString());

    console.log('Detection mode decision:', {
      mode: canUseClientSide ? 'client-side' : 'server-side',
      hasGoodMemory,
      hasGoodCores,
      webGLSupported,
      hasGoodNetwork,
    });

    return canUseClientSide;
  } catch (error) {
    console.error('Error checking device capabilities:', error);
    return false;
  }
};
